import React, { useEffect, useMemo, useState } from 'react'
import {useTranslation} from 'react-i18next';
import {
  Flex,
  Select,
  Table,
  message,
  Button,
  Checkbox,
  Upload,
  UploadProps,
  GetProp, Form, Input
} from "antd";
import Title from "antd/es/typography/Title";
import ButtonBlack from "../../ui/buttons/ButtonBlack";
import ButtonGreen from "../../ui/buttons/ButtonGreen";
import {Dayjs} from "dayjs";
import urls from "../../../api/urls";
import {api, HydraData} from "../../../api/apiProvider";
import useSWR from "swr";
import {useParams} from "react-router-dom";
import {
  BriefManager, BriefTask,
  BriefTaskKpiData,
  ModelType,
  Product,
  TaskStatus, TaskType
} from "../../../api/types";
import {DefaultOptionType} from "antd/es/select";
import IconUploadFile from "../../ui/icons/IconUploadFile";
import IconComments from "../../ui/icons/IconComments";
import Column from "antd/lib/table/Column";
import useSWRMutation from "swr/mutation";
import ButtonRed from "../../ui/buttons/ButtonRed";
import KpiDataFile from "./KpiDataFile";
import {isEqualValues} from "../../../utils";
import Roles from "../../../roles/Roles";

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]

interface Item {
  id: number;
  kpi: string;
  briefKpiTaskData: BriefTaskKpiData[];
  period: Dayjs[];
  manager: string;
  commentary: string;
}

interface Model {
  id: string,
  '@id': string,
  name: string,
}

interface BriefDataType {
  '@context': string,
  '@id': string,
  '@type': string,
  products: Product[],
  commentary: string,
  dataDateEnd: string,
  dataDateStart: string,
  dateEnd: string,
  id: string;
  manager?: string,
  name: string,
  product: Model,
  seasonalityText: string,
  tasks: string[],
  modelType: ModelType,
}

interface DataType extends Item {
  key: string;
  dataIndex: string;
}

interface BriefTasksProps {
  brief: BriefDataType,
  setSelectedCommentaryId: Function,
  selectedCommentaryId: Number,
  taskType: string,
  title: string,
}

const updateTask = async (id: number, data: any) => {
  return await api.patch(`${urls.briefTasks}/${id}`, {arg: data})
}

const deleteTask = async (id: number) => {
  return await api.delete(`${urls.briefTasks}/${id}`)
}

const addTaskData = async (data: any) => {
  return await api.post(urls.briefTasksData, {arg: data})
}

const updateTaskData = async (id: number, data: any) => {
  return await api.patch(`${urls.briefTasksData}/${id}`, {arg: data})
}

const uploadFile = async (formData: any) => {
  return await api.postFile(`${urls.mediaObjects}`, {arg: formData})
}

export default function BriefTaskFactors({
                                       brief, taskType, title, selectedCommentaryId, setSelectedCommentaryId
                                     }: BriefTasksProps) {
  const {t} = useTranslation()
  const routeParams = useParams()
  const briefId = Number(routeParams.id) || 0
  const [taskTypeIri, setTaskTypeIri] = useState<string>('')
  const [managersOptions, setManagersOptions] = useState<DefaultOptionType[]>([])
  const [statusesOptions, setStatusesOptions] = useState<DefaultOptionType[]>([])
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<Record<number, boolean>>([])
  const [selectedChannelsIds, setSelectedChannelsIds] = useState<number[]>([])
  const [isDataSaving, setIsDataSaving] = useState<boolean>(false)
  const [initialValues, setInitialValues] = useState<FormData[]>([])
  const [isValuesChanged, setIsValuesChanged] = useState<boolean>(false)

  const isEditable = () => Roles.hasAccess('canCreateOrUpdateBriefTasks')

  const {
    data: briefTasks,
    mutate: mutateBriefTasks,
  } = useSWR<{
    data: BriefTask[]
  }>(`${urls.briefTasks}?brief.id=${briefId}&taskType.code=${taskType}&t[]=taskBD&t[]=media_object:read`, api.get, {
    revalidateOnFocus: false,
  })

  const {
    data: managers,
    isValidating: isManagersValidating,
  } = useSWR<HydraData>(`${urls.briefs}/${briefId}/managers?t[]=manager`, api.get, {
    revalidateOnFocus: false
  })

  const {
    data: statuses,
  } = useSWR<HydraData>(urls.tasksStatuses, api.get, {
    revalidateOnFocus: false
  })

  const {
    trigger: createTask,
  } = useSWRMutation(urls.briefTasks, api.post)

  const {
    data: taskTypes,
  } = useSWR<{
    data: TaskType
  }>(`${urls.taskTypes}?code=${taskType}`, api.get, {
    revalidateOnFocus: false,
    dedupingInterval: 300,
  })

  useEffect(() => {
    if (taskTypes && taskTypes.data && Array.isArray(taskTypes.data) && taskTypes.data.length > 0) {
      setTaskTypeIri(taskTypes.data[0]['@id'])
    }
  }, [taskTypes])

  useEffect(() => {
    if (managers && managers.data) {
      setManagersOptions(managers.data.map((manager: BriefManager) => ({
        label: manager.manager.email,
        value: manager.manager['@id'],
      })))
    }
  }, [managers])

  useEffect(() => {
    if (statuses && statuses.data) {
      setStatusesOptions(statuses.data.map((status: TaskStatus) => ({
        label: status.name,
        value: status['@id'],
      })))
    }
  }, [statuses])

  useEffect(() => {
    if (briefTasks && briefTasks.data) {
      const newTasks = briefTasks.data.map((briefTask: BriefTask) => ({
        id: briefTask.id,
        key: String(briefTask.id),
        name: briefTask.name,
        product: briefTask.product,
        commentary: briefTask.commentary,
        briefTaskData: briefTask.briefTaskData,
        //period: [dayjs(briefTask.dataDateStart), dayjs(briefTask.dataDateEnd)],
        manager: briefTask.manager,
        source: '',
        taskDataId: briefTask.briefTaskData && briefTask.briefTaskData[0]
          && briefTask.briefTaskData[0].id || null,
        file: briefTask.briefTaskData && briefTask.briefTaskData[0]
        && briefTask.briefTaskData[0].file ? briefTask.briefTaskData[0].file : null,
        status: briefTask.briefTaskData && briefTask.briefTaskData[0]
        && briefTask.briefTaskData[0].status ? briefTask.briefTaskData[0].status : null,
      }))

      const files: Record<number, boolean> = []
      for (let key in briefTasks.data) {
        const briefTaskKpi = briefTasks.data[key]
        if (!briefTaskKpi.briefTaskData) {
          continue
        }
        for (let taskData of briefTaskKpi.briefTaskData) {
          if (taskData.file) {
            files[key] = true
          }
        }
      }

      setFileList(files)

      form.setFieldValue('factors', newTasks)
    }
  }, [briefTasks])

  const isBriefManyProducts = () => {
    return brief && brief.modelType && brief.modelType.isBriefManyProducts
  }

  const productsOptions = useMemo(() => {
    if (!isBriefManyProducts()) {
      return []
    }

    return brief.products.map((product: Product) => {
      return {
        value: product['@id'],
        label: product.name,
      }
    })
  }, [brief])

  const onFinish = async (values: any) => {
    setIsDataSaving(true)
    for (let index in values.factors) {
      const taskData = values.factors[index]
      let product = taskData.product

      if (!isBriefManyProducts() && brief.products && brief.products[0]) {
        product = brief.products[0]["@id"] || ''
      } else if (isBriefManyProducts()) {
        product = taskData.product
      }

      const data = {
        name: taskData.name || `${t('managerTasks.defaultTaskName')} ${brief.products[0]?.name}`,
        brief: brief["@id"],
        taskType: taskTypeIri,
        manager: taskData.manager,
        product: product,
        dataDateStart: brief.dataDateStart,
        dataDateEnd: brief.dataDateEnd,
      }

      let mediaFactorId = taskData.id

      if (mediaFactorId > 0) {
        await updateTask(mediaFactorId, data).catch(errors => {
          if (errors['hydra:description']) {
            message.error(errors['hydra:description'])
          }
        })
      } else {
        const response = await createTask(data).then(response => response).catch(errors => {
          if (errors['hydra:description']) {
            message.error(errors['hydra:description'])
          }
        })

        if (response && response.id) {
          mediaFactorId = response.id
        }
      }

      if (!mediaFactorId) {
        setIsDataSaving(false)
        setIsValuesChanged(false)
        return
      }

      let fileId = taskData.file && taskData.file['@id'] ? taskData.file['@id'] : null

      if (taskData.file && taskData.file[0] && taskData.file[0].uid) {
        const formData = new FormData()

        formData.append('file', taskData.file[0] as FileType);

        const response = await uploadFile(formData).then(response => response).catch(errors => {
          if (errors['hydra:description']) {
            message.error(errors['hydra:description'])
          }
        })

        if (response && response['@id']) {
          fileId = response['@id']
        }
      }

      if (taskData.taskDataId) {
        await updateTaskData(taskData.taskDataId, {
          status: taskData.status,
          file: taskData.file && taskData.file['@id']
            ? taskData.file['@id']
            : fileId,
        })
      } else {
        await addTaskData({
          file: fileId,
          briefTask: `/api${urls.briefTasks}/${mediaFactorId}`,
        }).catch(errors => {
          if (errors['hydra:description']) {
            message.error(errors['hydra:description'])
          }
        })
      }
    }

    message.success(t('common.saved'))
    setFileList([])
    mutateBriefTasks()
    setIsDataSaving(false)
    setIsValuesChanged(false)
  }

  const deleteTasks = async () => {
    let deletedCount = 0
    let factors = form.getFieldValue('factors')

    for (let index of selectedChannelsIds) {
      const record = factors[index]
      if (record && record.id) {
        await deleteTask(record.id).then(() => {
          deletedCount++
        }).catch(errors => {
          if (errors['hydra:description']) {
            message.error(errors['hydra:description'])
          }

          return
        })
      } else {
        factors.splice(index, 1)
      }
    }

    form.setFieldValue('factors', factors)

    if (deletedCount > 0) {
      message.success(t('common.deleted'))
      mutateBriefTasks()
    }

    setSelectedChannelsIds([])
  }

  const getRowClassName = (index: number, record: any) => {
    if (selectedChannelsIds.includes(index)) {
      return 'selected-task-row'
    }

    const val = form.getFieldValue(['factors', index, "status"])
    const status = statuses && statuses.data.find((item: TaskStatus) => {
      return item["@id"] === val
    })

    if (status && status.name === 'принято') {
      return 'accepted-task-row'
    }

    return ''
  }

  const onValuesChange = (changedValues: any, allValues: any) => {
    if (allValues.length !== initialValues?.length) {
      setIsValuesChanged(true)
      return
    }

    for (let key in initialValues) {
      if (!initialValues.hasOwnProperty(key)) {
        continue
      }

      const initialValue = initialValues[Number(key)]
      if (key in allValues) {
        const changedValue = allValues[key]

        if (!isEqualValues(initialValue, changedValue)) {
          setIsValuesChanged(true)
          return
        }
      }
    }

    setIsValuesChanged(false)
  }

  return (
    <Flex vertical gap={'middle'}>
      <Title level={5}>{title}</Title>
      <Form
        form={form}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
      >
        <Form.List name="factors">
          {(factors, {add, remove}) => {
            return <Table
              bordered
              dataSource={factors}
              rowKey={'key'}
              scroll={{x: 800}}
              pagination={false}
              size={'middle'}
              footer={() => {
                return (
                  <Form.Item>
                    <Flex justify={'space-between'}>
                      <Flex gap={'small'}>
                        <ButtonGreen
                          onClick={add}
                          disabled={!isEditable()}
                        >{t('common.add')}</ButtonGreen>
                        {selectedChannelsIds && selectedChannelsIds.length > 0 &&
                          <ButtonRed onClick={deleteTasks}>{t('common.delete')}</ButtonRed>
                        }
                      </Flex>
                      <ButtonBlack
                        onClick={form.submit}
                        loading={isDataSaving}
                        disabled={!isValuesChanged || !isEditable()}
                      >{t('common.save')}</ButtonBlack>
                    </Flex>
                  </Form.Item>
                )
              }}
              rowClassName={(record, index) => getRowClassName(index, record)}
            >
              <Column
                dataIndex={"id"}
                title={"№"}
                render={
                  (value: any, record: DataType, index: number) => {
                    return <div>{index + 1}</div>
                  }
                }
                width={50}
              />
              <Column
                dataIndex={"actions"}
                title={""}
                render={
                  (value: any, record: DataType, index: number) => {
                    return <Checkbox
                      checked={selectedChannelsIds.includes(index)}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setSelectedChannelsIds([
                            ...selectedChannelsIds,
                            index,
                          ])
                        } else {
                          setSelectedChannelsIds([...selectedChannelsIds.filter(function (value, valueIndex, arr) {
                            return value !== index
                          })])
                        }
                      }}
                      disabled={!isEditable()}
                    />
                  }
                }
                width={30}
              />

              {isBriefManyProducts() &&
                <Column
                  dataIndex={"product"}
                  title={t('brief.product')}
                  render={
                    (value: any, record: DataType, index: number) => {
                      return (
                        <Form.Item name={[index, "product"]} rules={[{required: true, message: t('common.choose')}]}>
                          <Select
                            value={value}
                            style={{width: '100%'}}
                            options={productsOptions}
                          />
                        </Form.Item>)
                    }
                  }
                  width={400}
                />
              }

              <Column
                dataIndex={"name"}
                title={t('managerTasks.name')}
                render={
                  (value: any, record: DataType, index: number) => {
                    return (<Form.Item name={[index, "name"]}>
                      <Input
                        value={value}
                        style={{width: '100%'}}
                        disabled={!isEditable()}
                      />
                    </Form.Item>)
                  }
                }
                width={400}
              />
              <Column
                dataIndex={"manager"}
                title={t('managerTasks.responsible')}
                render={
                  (value: any, record: DataType, index: number) => {
                    return (
                      <Form.Item name={[index, "manager"]} rules={[{required: true, message: t('common.choose')}]}>
                        <Select
                          value={value}
                          style={{width: '100%'}}
                          options={managersOptions}
                          disabled={!isEditable()}
                        />
                      </Form.Item>)
                  }
                }
                width={400}
              />
              <Column
                dataIndex={"file"}
                title={t('managerTasks.dataSource')}
                render={
                  (value: any, record: DataType, index: number) => {
                    const file = form.getFieldValue(['factors', index, "file"])

                    return <Flex vertical gap={'small'}
                                 id={taskType + '-file-wrapper' + index}
                    >
                      <Form.Item
                        shouldUpdate
                      >
                        {() => {

                          return (
                            <>
                              {file && file.id &&
                                <div key={file.id} style={{height: '32px'}}>
                                  <KpiDataFile
                                    key={String(index)}
                                    fileUrl={file.contentUrl}
                                    fileName={file.originalName}
                                    inputId={taskType + '-file-wrapper' + index}
                                    onRemove={() => {
                                      form.setFieldValue(['factors', index, "file"], null)
                                      const newFileList = fileList
                                      newFileList[index] = false
                                      setFileList(newFileList)
                                      form.setFieldsValue({})
                                      setIsValuesChanged(true)
                                    }}
                                    editable={isEditable()}
                                  />
                                </div>
                              }
                              {(!file || fileList[index]) &&
                                <Upload
                                  id={'file' + index}
                                  beforeUpload={file => {
                                    form.setFieldValue(['factors', index, "file"], [file])
                                    const newFileList = fileList;
                                    newFileList[index] = true
                                    setFileList(newFileList)
                                    form.setFieldsValue({})
                                    setIsValuesChanged(true)
                                    return false
                                  }}
                                  maxCount={1}
                                  style={{width: '100%'}}
                                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                  itemRender={(originNode, file) => {
                                    return fileList[index] ? <KpiDataFile
                                      key={String(index)}
                                      fileUrl={file.url}
                                      fileName={file.name}
                                      inputId={taskType + 'file-wrapper' + index}
                                      onRemove={() => {
                                        form.setFieldValue(['factors', index, "file"], null)
                                        const newFileList = fileList
                                        newFileList[index] = false
                                        setFileList(newFileList)
                                        form.setFieldsValue({})
                                        setIsValuesChanged(true)
                                      }}
                                      editable
                                    /> : ''
                                  }}
                                  disabled={!isEditable()}
                                >
                                  {!fileList[index] &&
                                    <Button
                                      icon={<IconUploadFile/>}
                                      size={'small'}
                                      disabled={!isEditable()}
                                    >{t('managerTasks.upload')}</Button>
                                  }

                                </Upload>
                              }
                            </>
                          )
                        }}

                      </Form.Item>
                    </Flex>
                  }
                }
                width={212}
              />
              <Column
                dataIndex={"status"}
                title={t('managerTasks.status')}
                render={
                  (value: any, record: DataType, index: number) => {
                    return (<Form.Item name={[index, "status"]}>
                      <Select
                        value={value}
                        style={{width: 160}}
                        options={statusesOptions}
                        disabled={!Roles.hasAccess('canSetBriefTasksStatus')}
                      />
                    </Form.Item>)
                  }
                }
                fixed={'right'}
                width={170}
              />
              <Column
                dataIndex={"commentary"}
                title={t('managerTasks.comments')}
                render={
                  (value: any, record: DataType, index: number) => {
                    const item = form.getFieldValue(['factors', index, "commentary"])
                    return <>
                      {item &&
                        <div className={'media-factors__comments'}
                             onClick={() => {
                               if (!isEditable()) {
                                 return
                               }

                               if (Number(item.split('/').pop()) === selectedCommentaryId) {
                                 setSelectedCommentaryId(0)
                               } else {
                                 setSelectedCommentaryId(Number(item.split('/').pop()))
                               }
                             }}>
                          <IconComments/>
                        </div>
                      }
                    </>
                  }
                }
                fixed={'right'}
                width={100}
              />

            </Table>
          }}
        </Form.List>
      </Form>
    </Flex>
  )
}
